import { Button as RACButton } from 'react-aria-components';

import { timeAgo } from '@/spaces/campaigns/strategyFormat';
import { cn } from '@/utils/cn';

import type { StrategyListItem } from '../../../../../bridge/types/strategy.ts';

import { flagsNewestFirst, openFlags } from './strategyDockData';

const KIND_LABEL: Record<string, string> = {
	approval_requested: 'Approval requested',
	manual_metric_needed: 'Metric needed',
	target_hit_action: 'Target reached',
	stalled_owner_steps: 'Steps stalled',
	// The same words the ask's own panel uses for its title, so one thing keeps one name.
	owner_action_needed: 'Your part',
	campaign_setup_needed: 'Campaign ready to set up',
	// NOT "ready to set up". That label is a claim about readiness, and this campaign failed to
	// build, so reusing it would tell the owner the opposite of what happened.
	campaign_setup_failed: 'Campaign could not be set up',
	strategy_brief: 'Strategy brief',
};

export function FlagList({
	strategyId,
	items,
	onAcknowledge,
	onOpenPanel,
	onOpenOwnerAsk,
	onOpenCampaignSetup,
	onBack,
}: {
	strategyId: string;
	items: StrategyListItem[];
	onAcknowledge: (flagId: string) => void;
	onOpenPanel: (flagId: string) => void;
	onOpenOwnerAsk: (actionId: string, message: string) => void;
	onOpenCampaignSetup: (
		processId: string,
		message: string,
		title: string,
	) => void;
	onBack: () => void;
}) {
	const item = items.find((s) => s.id === strategyId);
	// Only what still needs the owner, newest first. An ask stays until its answer arrives,
	// so reading it keeps it here. A note that only shares news goes once the owner has read
	// it, which is the one dismissal in this list.
	const flags = item ? flagsNewestFirst(openFlags(item)) : [];

	return (
		<div className="flex min-h-0 w-full flex-1 flex-col px-5">
			<div className="flex flex-shrink-0 items-center gap-3 pb-4">
				<RACButton
					onPress={onBack}
					aria-label="Back to list"
					className="grid size-7 shrink-0 cursor-pointer place-items-center
						rounded-full text-(--text-muted) outline-none
						hover:bg-(--glass-bg-hover) hover:text-(--text-primary)"
				>
					<svg
						viewBox="0 0 16 16"
						width={14}
						height={14}
						fill="none"
						stroke="currentColor"
						strokeWidth={2}
						strokeLinecap="round"
						strokeLinejoin="round"
						aria-hidden
					>
						<path d="M10 3 5 8l5 5" />
					</svg>
				</RACButton>
				<span
					className="text-sm font-semibold tracking-wide text-(--text-muted)
						uppercase"
				>
					Notifications
				</span>
			</div>
			<div className="scrollbar-hidden min-h-0 flex-1 overflow-y-auto pb-5">
				{flags.length === 0 ? (
					<div
						className="flex flex-col items-center justify-center gap-2 py-12
							text-center"
					>
						<p className="text-base font-semibold text-(--text-primary)">
							All caught up
						</p>
						<p className="text-sm text-(--text-muted)">
							No notifications right now.
						</p>
					</div>
				) : (
					<div className="flex flex-col gap-3">
						{flags.map((flag) => (
							<RACButton
								key={flag.id}
								data-testid={`flag-card-${flag.id}`}
								data-acknowledged={flag.acknowledged_at ? 'true' : 'false'}
								data-kind={flag.kind}
								data-resolved={flag.resolved_at ? 'true' : 'false'}
								onPress={() => {
									onAcknowledge(flag.id);
									// A built campaign has no chat thread yet. This press is what
									// creates it, so it runs before the owner-ask branch: this
									// flag carries a process, never an action.
									if (flag.process_id) {
										// The CAMPAIGN's name titles the thread. Passing the
										// notification's label would title the thread
										// "Campaign ready to set up", which names the
										// notification and not the work.
										onOpenCampaignSetup(
											flag.process_id,
											flag.message,
											flag.campaign_title || '',
										);
										return;
									}
									// An owner ask is answered against the action it stands for,
									// so it opens a thread keyed by that action id. Every other
									// request opens a thread keyed by the request itself.
									if (flag.action_id) {
										onOpenOwnerAsk(flag.action_id, flag.message);
										return;
									}
									onOpenPanel(flag.id);
								}}
								className={cn(
									`glass-card-sm flex w-full cursor-pointer flex-col gap-1.5 p-4
										text-left transition-transform outline-none
										hover:scale-[1.01] active:scale-[0.99]
										motion-reduce:transition-none motion-reduce:hover:scale-100
										motion-reduce:active:scale-100`,
									// The card is now the only route to an unanswered request, so it
									// must be reachable and visible by keyboard.
									'focus-visible:shadow-[0_0_0_3px_rgba(59,99,251,0.18)]',
								)}
							>
								<div className="flex items-center gap-2">
									{/* The unread dot is the state carrier, never colour alone: an
									    answered request keeps its label and message and only loses
									    the dot. Same recipe as the NavBar unread mark. */}
									{!flag.acknowledged_at && (
										<span
											aria-hidden
											className="bg-beacon-unread size-1.5 shrink-0
												rounded-full"
										/>
									)}
									<span
										className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px]
											font-semibold text-amber-700"
									>
										{KIND_LABEL[flag.kind] ?? flag.kind}
									</span>
									{flag.since && (
										<span className="text-xs text-(--text-muted)">
											{timeAgo(flag.since)}
										</span>
									)}
								</div>
								<p className="text-sm leading-snug text-(--text-primary)">
									{flag.message}
								</p>
							</RACButton>
						))}
					</div>
				)}
			</div>
		</div>
	);
}
